<template>
  <Text :variant="textVariant" class="user-pic" :class="[userColor, size]">
    <template v-if="!showImage">{{ userInitials }}</template>
    <!-- Opaque, covers the initials when it loads; on error it stays hidden and the initials show -->
    <img
      v-if="showImage"
      :src="props.imageUrl ?? undefined"
      :alt="props.userName"
      class="avatar-image"
      @error="imageFailed = true"
    />
    <div v-if="iconName" :class="['icon-status', status]">
      <Icon :name="iconName" class="icon" />
    </div>
  </Text>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { IconName } from '../../values/icons.const';
import Icon from '../icon.vue';
import Text from '../text.vue';
import { getAvatarColor } from './avatar-color.function';
import { AvatarProps } from './avatar-props';
import { AvatarStatus } from './avatar-status';

const props = withDefaults(defineProps<AvatarProps>(), {
  showSingleLetter: false,
  size: 'medium',
});

const imageFailed = ref(false);

// A new URL gets a fresh chance to load
watch(
  () => props.imageUrl,
  () => {
    imageFailed.value = false;
  },
);

const showImage = computed(() => Boolean(props.imageUrl) && !imageFailed.value);

const userInitials = computed(() => {
  let names = props.userName.toUpperCase().split(' ');

  if (props.showSingleLetter) {
    return names[0].charAt(0);
  }

  const firstTwoNames = names.slice(0, 2);
  return firstTwoNames.map((name) => name.charAt(0)).join('');
});

const iconsMap = new Map<AvatarStatus, IconName>([
  ['accepted', 'check'],
  ['pending', 'question-mark'],
  ['declined', 'x-close'],
]);

const iconName = computed(() => iconsMap.get(props.status!));
const textVariant = computed(() => (props.size === 'large' ? 'label-large' : 'label-small'));
// Shared with every other surface that draws this person — see getAvatarColor.
const userColor = computed(() => getAvatarColor(props.userName));
</script>

<style lang="scss" scoped>
.user-pic {
  color: var(--t-color-neutral-950);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--t-border-radius-full-rounded);
  position: relative;

  // Rounded on the image itself: overflow:hidden on the container would clip the corner status icon
  .avatar-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;
  }

  &.small {
    --avatar-size: 1.5rem;
    min-width: var(--avatar-size);
    width: var(--avatar-size);
    height: var(--avatar-size);
  }

  &.medium {
    --avatar-size: 2.25rem;
    min-width: var(--avatar-size);
    width: var(--avatar-size);
    height: var(--avatar-size);
  }

  &.large {
    --avatar-size: 3rem;
    min-width: var(--avatar-size);
    width: var(--avatar-size);
    height: var(--avatar-size);
  }

  &.blue {
    background-color: var(--t-color-blue-200);
  }

  &.green {
    background-color: var(--t-color-green-200);
  }

  &.violet {
    background-color: var(--t-color-violet-200);
  }

  &.orange {
    background-color: var(--t-color-orange-200);
  }

  &.yellow {
    background-color: var(--t-color-yellow-200);
  }

  .icon-status {
    --icon-status-size: 14px;
    --icon-status-border-size: 1px;

    position: absolute;
    bottom: -1px;
    right: -1px;

    width: var(--icon-status-size);
    height: var(--icon-status-size);

    display: flex;
    justify-content: center;
    align-items: center;

    border: var(--icon-status-border-size) solid #fff;
    border-radius: var(--t-border-radius-half-rounded);
    color: var(--t-color-neutral-white);

    &.accepted {
      background-color: var(--t-color-green-500);
    }

    &.pending {
      background-color: var(--t-color-neutral-500);
    }

    &.declined {
      background-color: var(--t-color-red-400);
    }

    .icon {
      --t-icon-size: var(--t-spacing-sm) !important;
    }
  }
}
</style>
